import React, { useState } from 'react';
import VendorForm from '../components/VendorForm';
import CategoryForm from '../components/CategoryForm';
import SKUForm from '../components/SKUForm';
import BudgetForm from '../components/BudgetForm';

/**
 * SVG icon components for setup tabs.
 * Inline SVG ensures crisp rendering and proper color inheritance from tab styling.
 */
const TabIcons = {
  vendors: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  categories: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  ),
  sku: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" />
    </svg>
  ),
  budgets: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
};

const tabs = [
  { id: 'vendors', label: 'Vendors', icon: TabIcons.vendors },
  { id: 'categories', label: 'Categories', icon: TabIcons.categories },
  { id: 'sku', label: 'SKU Catalog', icon: TabIcons.sku },
  { id: 'budgets', label: 'Budgets', icon: TabIcons.budgets },
];

/**
 * Setup page with tabbed interface for managing businesses, vendors, categories, SKU catalog, and budgets.
 *
 * @param {Object} props
 * @param {Function} [props.onBusinessUpdate] - Callback when a business is created, deleted, or toggled
 */
function Setup() {
  const [activeTab, setActiveTab] = useState('vendors');

  const handleKeyDown = (e, currentTabId) => {
    const tabIds = tabs.map(t => t.id);
    const currentIndex = tabIds.indexOf(currentTabId);

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : tabIds.length - 1;
      setActiveTab(tabIds[prevIndex]);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      const nextIndex = currentIndex < tabIds.length - 1 ? currentIndex + 1 : 0;
      setActiveTab(tabIds[nextIndex]);
    }
  };

  return (
    <div className="setup">
      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title">Setup</h1>
        <p className="page-subtitle">Configure your vendors, categories, SKU catalog, and budgets</p>
      </div>

      {/* Tabs */}
      <div className="tabs" role="tablist">
        {tabs.map(tab => (
          <button
            key={tab.id}
            id={`tab-${tab.id}`}
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`tab ${activeTab === tab.id ? 'tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            onKeyDown={(e) => handleKeyDown(e, tab.id)}
          >
            <span>{tab.icon}</span> {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'vendors' && <div role="tabpanel" aria-labelledby="tab-vendors"><VendorForm /></div>}
      {activeTab === 'categories' && <div role="tabpanel" aria-labelledby="tab-categories"><CategoryForm /></div>}
      {activeTab === 'sku' && (
        <div role="tabpanel" aria-labelledby="tab-sku">
          {/* Phase 11 Wave 6b — Normalized $ curation hint. The Base unit + Base
              units/pack fields rendered inside SKUForm (Wave 7) drive the
              Normalized $ column shown in Analytics → Cross-Vendor Price
              Comparison and SuperAdmin → Data Browser → Line items / SKU catalog.
              Tells operators why those two extra fields matter. */}
          <p className="hint">
            Curate <strong>Base unit</strong> and <strong>Base units/pack</strong> on each SKU to enable Normalized $ display in Analytics → Cross-Vendor Price Comparison and the cross-org Data Browser. Rows without curation render as &mdash; until you fill those values in.
          </p>
          <SKUForm />
        </div>
      )}
      {activeTab === 'budgets' && <div role="tabpanel" aria-labelledby="tab-budgets"><BudgetForm /></div>}
    </div>
  );
}


export default Setup;
